/**
 * /onboard/setup/import/:runId/inferences
 *
 * Read-only review page. Loads shop_setup_inferences +
 * technician_inferred_profile for the shop, renders the
 * InferenceReviewCards, and offers a Continue button that
 * navigates to the prefilled wizard.
 *
 * Gated by importEnabledForInternal.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { ImportProgressPanel } from '@/components/onboarding/import/ImportProgressPanel';
import { InferenceReviewCards } from '@/components/onboarding/import/InferenceReviewCards';
import { useImportRun } from '@/hooks/onboarding/useImportRun';
import type {
  SetupInferenceRow,
  TechnicianInferredProfileRow,
} from '@/lib/onboarding/inferenceLoader';

const INFERENCE_TABLE = 'shop_setup_inferences' as unknown as Parameters<typeof supabase.from>[0];
const TECH_PROFILE_TABLE =
  'technician_inferred_profile' as unknown as Parameters<typeof supabase.from>[0];

interface InferencesData {
  inferences: SetupInferenceRow[];
  technicianProfiles: TechnicianInferredProfileRow[];
}

async function fetchInferences(shopId: string): Promise<InferencesData> {
  // Same untyped-table reason as useImportRun — the shop_setup_*
  // and technician_inferred_profile tables aren't in the generated
  // Database type yet.
  // deno-lint-ignore no-explicit-any
  const client = supabase as any;
  const [inf, prof] = await Promise.all([
    client
      .from(INFERENCE_TABLE)
      .select('inference_kind, confidence, payload')
      .eq('shop_id', shopId),
    client
      .from(TECH_PROFILE_TABLE)
      .select(
        'external_technician_id, display_name, job_count, total_labor_hours, median_labor_hours, comeback_rate, warranty_rate, appears_full_time, top_jobs',
      )
      .eq('shop_id', shopId)
      .eq('is_stale', false),
  ]);
  if (inf.error) throw inf.error;
  if (prof.error) throw prof.error;
  return {
    inferences: ((inf.data ?? []) as unknown) as SetupInferenceRow[],
    technicianProfiles: ((prof.data ?? []) as unknown) as TechnicianInferredProfileRow[],
  };
}

const OnboardSetupImportInferences = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const { activeShop, user } = useAuth();
  const importEnabled = useFeatureFlag('importEnabledForInternal');

  const { run, isLoading: isRunLoading } = useImportRun({
    shopId: activeShop?.id ?? null,
    importRunId: runId ?? null,
    enabled: Boolean(importEnabled && activeShop?.id && runId),
  });

  const inferencesQuery = useQuery({
    queryKey: ['shop_setup_inferences', activeShop?.id],
    enabled: Boolean(importEnabled && activeShop?.id),
    queryFn: () => fetchInferences(activeShop!.id),
  });

  useEffect(() => {
    if (importEnabled === false) {
      navigate('/onboard/setup', { replace: true });
    }
  }, [importEnabled, navigate]);

  if (!user || !activeShop) return null;
  if (!runId) {
    return (
      <div className="mx-auto max-w-2xl p-4" data-testid="inferences-missing-run">
        <Card>
          <CardHeader>
            <CardTitle>Import not found</CardTitle>
            <CardDescription>Missing run id. Return to setup and try again.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const data = inferencesQuery.data;
  return (
    <div
      className="mx-auto w-full max-w-4xl space-y-6 p-4"
      data-testid="onboard-import-inferences-page"
    >
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Review what we learned</h1>
        <p className="text-sm text-muted-foreground">
          These cards summarize what your historical import suggests. None of this has been
          written to your live shop yet — review here and confirm everything on the next step.
        </p>
      </div>

      <ImportProgressPanel run={run} isLoading={isRunLoading} />

      {inferencesQuery.isLoading && (
        <div className="text-sm text-muted-foreground" data-testid="inferences-loading">
          Loading inferences…
        </div>
      )}
      {inferencesQuery.isError && (
        <Card data-testid="inferences-load-error">
          <CardContent className="py-3 text-sm text-destructive">
            Could not load inferences. Refresh to retry.
          </CardContent>
        </Card>
      )}

      {data && (
        <InferenceReviewCards
          inferences={data.inferences}
          technicianProfiles={data.technicianProfiles}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="outline"
          onClick={() => navigate(`/onboard/setup/import/${runId}/preview`)}
          data-testid="inferences-back"
        >
          Back to import status
        </Button>
        <Button
          onClick={() => navigate(`/onboard/setup/import/${runId}/wizard`)}
          disabled={!data}
          data-testid="inferences-continue"
        >
          <ArrowRight className="mr-2 h-4 w-4" />
          Continue to setup
        </Button>
      </div>
    </div>
  );
};

export default OnboardSetupImportInferences;
